import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UnsubscribePage() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ready | already | done | error
  const [info, setInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/unsubscribe/${token}`);
        if (cancelled) return;
        setInfo(data);
        setStatus(data.already_unsubscribed ? 'already' : 'ready');
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err.response?.data?.detail || 'This unsubscribe link is invalid or has expired.');
        setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/unsubscribe/${token}`);
      setInfo(data);
      setStatus('done');
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Something went wrong. Please try again.');
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="unsubscribe-page" className="min-h-[70vh] bg-[#F4F4F5] flex items-center justify-center px-4 py-20">
      <SEO
        title="Unsubscribe"
        description="Unsubscribe from Glossop Round Table marketing emails."
        path={`/unsubscribe/${token || ''}`}
        noIndex
      />
      <div className="w-full max-w-xl bg-white border-l-4 border-[#F0A500] p-8 sm:p-10 shadow-sm">
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-2">
          Email Preferences
        </span>

        {status === 'loading' && (
          <p data-testid="unsubscribe-loading" className="text-[#52525B]">Checking your link…</p>
        )}

        {status === 'ready' && info && (
          <>
            <h1 className="text-3xl font-bold text-[#0A0A0A] mb-3">Unsubscribe from Bonfire Night updates?</h1>
            <p className="text-base text-[#52525B] leading-relaxed mb-6">
              We&rsquo;ll stop emailing <strong className="text-[#0A0A0A]">{info.email}</strong> about
              Bonfire Night 2026 immediately. You won&rsquo;t hear from us about this again unless you sign up again.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={submitting}
                data-testid="unsubscribe-confirm-button"
                className="px-6 py-3 bg-[#0A0A0A] hover:bg-zinc-800 disabled:opacity-60 text-white font-bold uppercase tracking-wider text-sm transition-colors"
              >
                {submitting ? 'Unsubscribing…' : 'Yes, unsubscribe me'}
              </button>
              <Link
                to="/"
                data-testid="unsubscribe-cancel-link"
                className="px-6 py-3 bg-zinc-200 hover:bg-zinc-300 text-[#0A0A0A] font-semibold text-sm transition-colors text-center"
              >
                No, keep me on the list
              </Link>
            </div>
          </>
        )}

        {status === 'already' && info && (
          <>
            <h1 className="text-3xl font-bold text-[#0A0A0A] mb-3" data-testid="unsubscribe-already">You&rsquo;re already unsubscribed</h1>
            <p className="text-base text-[#52525B] leading-relaxed mb-6">
              <strong className="text-[#0A0A0A]">{info.email}</strong> has already been removed from the
              Bonfire Night 2026 list. You won&rsquo;t receive any further emails from us about this.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-[#F0A500] hover:bg-[#d99400] text-[#0A0A0A] font-bold uppercase tracking-wider text-sm transition-colors"
            >
              Back to the website
            </Link>
          </>
        )}

        {status === 'done' && info && (
          <>
            <h1 className="text-3xl font-bold text-[#0A0A0A] mb-3" data-testid="unsubscribe-success">You&rsquo;ve been unsubscribed</h1>
            <p className="text-base text-[#52525B] leading-relaxed mb-6">
              We&rsquo;ve removed <strong className="text-[#0A0A0A]">{info.email}</strong> from the
              Bonfire Night 2026 launch list. Sorry to see you go &mdash; if you change your mind, you can
              sign up again any time on the Bonfire Night page.
            </p>
            <Link
              to="/bonfire"
              className="inline-block px-6 py-3 bg-[#F0A500] hover:bg-[#d99400] text-[#0A0A0A] font-bold uppercase tracking-wider text-sm transition-colors"
            >
              Visit the Bonfire Night page
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="text-3xl font-bold text-[#0A0A0A] mb-3" data-testid="unsubscribe-error">Link not recognised</h1>
            <p className="text-base text-[#52525B] leading-relaxed mb-6">{errorMsg}</p>
            <p className="text-sm text-[#52525B] mb-6">
              If you&rsquo;d like to be removed from our list, please email{' '}
              <a href="mailto:membership@glossoproundtable.org" className="text-[#F0A500] hover:underline">
                membership@glossoproundtable.org
              </a>{' '}
              and we&rsquo;ll take care of it manually.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-zinc-200 hover:bg-zinc-300 text-[#0A0A0A] font-semibold text-sm transition-colors"
            >
              Back to the website
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
